import React, { useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ModalActions } from "src/redux/Modal/actions";
import { ConfirmActions } from "src/redux/Confirm/actions";
import Button from "src/components/common/Button";
import empty from "is-empty";
import SearchSecurity from "./SearchSecurity";
import { MdActions } from "src/redux/MarketData/actions";
import { SettingsSelectors } from "src/redux/Settings/selectors";
import { AuthSelectors } from "src/redux/Auth/selectors";
import { SpinnerActions } from "src/redux/Spinner/actions";
import { SettingsActions } from "src/redux/Settings/actions";
import {
    defaultColDef,
    getDetailBlockedSecuritiesListColumnDefs,
    getRowClass,
} from "./column_defs";
import styles from "./styles.module.scss";
import { AgGridReact } from "ag-grid-react";
import { store } from "src/index";
import isEmpty from "is-empty";

class DeleteButton {
    init(params) {
        this.eGui = document.createElement("div");
        this.eGui.className = styles.deleteBtn;
        this.element = document.createElement("div");
        this.element.className = `${styles.delete} delete-btn`;
        // setAttribute keeps the (user-authored) security name inert - never parsed as HTML.
        this.element.setAttribute("data-testid", `delete-${params.data?.name}`);
        this.element.setAttribute("aria-label", `Delete ${params.data?.name}`);
        this.element.onclick = () => this.handleDelete(params);
        this.eGui.appendChild(this.element);
    }

    async handleDelete(params) {
        await store.dispatch(
            SettingsActions.RemovePendingBlockedSecurity(params.data.id)
        );
    }

    getGui() {
        return this.eGui;
    }
}

export default () => {
    const dispatch = useDispatch();
    const isTraderRole = useSelector(AuthSelectors.IsTraderRole);

    const [refreshTable, setRefreshTable] = useState(null);

    const blockedSecuritiesSettings = useSelector(
        SettingsSelectors.LenderPendingBlockedSecuritiesWithMarketData
    );

    const addBlockedSecurity = () => {
        dispatch(
            ModalActions.Open(SearchSecurity, {
                width: 500,
                height: 500,
                ignoreClickOutside: true,
                title: "Search Security",
                settings: true,
                customProps: {
                    currentAndPendingSecurityBbgTickers: new Set(
                        blockedSecuritiesSettings.map((e) => e.BbgTicker)
                    ),
                    onSelect: async (stockTruId) => {
                        if (!isEmpty(stockTruId)) {
                            await dispatch(SpinnerActions.Open());
                            const stockData = await dispatch(
                                MdActions.GetMarketData(stockTruId)
                            );
                            await dispatch(SpinnerActions.Close());

                            await dispatch(
                                SettingsActions.AddPendingBlockedSecurity(
                                    stockData.result.data
                                )
                            );
                        }

                        dispatch(ModalActions.Close());
                    },
                },
            })
        );
    };

    const handleRowDataChanged = (e) => {
        if (!refreshTable || !listGridApi || empty(blockedSecuritiesSettings))
            return;

        const index = blockedSecuritiesSettings.length - 1;
        listGridApi.ensureIndexVisible(index, "bottom");
        setRefreshTable(false);
    };

    const detailBlockedSecuritiesListColumnDefs = useMemo(
        () => getDetailBlockedSecuritiesListColumnDefs(),
        []
    );

    const [listGridApi, setListGridApi] = useState(null);

    const handleListGridReady = (params) => {
        setListGridApi(params.api);
        params.api.sizeColumnsToFit();
    };

    const contextParams = {
        blockedSecuritiesSettings,
    };

    return (
        <div className={styles.blockedSecuritiesSettings}>
            <div className={styles.section}>
                <div className={styles.title}>
                    Manage securities subject to your internal restrictions.
                </div>

                <div className={styles.footnote}>
                    Securities added to this list will be restricted from use
                    within the relevant functions of the service. This feature
                    is intended to support your organization’s internal control
                    requirements, including temporary restrictions arising from
                    corporate actions, compliance policies, or other internal
                    considerations.
                </div>

                <div className={styles.footnote}>
                    Securities will remain on the restricted list until removed
                    by an authorized user.
                </div>

                <div className={styles.footnote}>
                    Changes must be saved to take effect.
                </div>

                <div className={styles.contentWrapper}>
                    <div className={styles.list} data-testid="list-settings">
                        <div
                            className={[
                                styles.table,
                                "ag-theme-alpine-dark",
                            ].join(" ")}
                        >
                            <AgGridReact
                                suppressCellFlash={true}
                                suppressFlash={true}
                                rowBuffer={50}
                                rowHeight={40}
                                headerHeight={40}
                                getRowClass={getRowClass}
                                rowData={blockedSecuritiesSettings}
                                onGridReady={handleListGridReady}
                                context={contextParams}
                                getRowId={(params) => params.data.id}
                                defaultColDef={defaultColDef}
                                columnDefs={
                                    detailBlockedSecuritiesListColumnDefs
                                }
                                components={{
                                    deleteButton: DeleteButton,
                                }}
                                enableCellChangeFlash={true}
                                onRowDataChanged={handleRowDataChanged}
                                suppressScrollOnNewData={true}
                                suppressContextMenu={true}
                                scrollbarWidth={8}
                                suppressHorizontalScroll={true}
                            />
                        </div>
                    </div>

                    <div className={styles.addButton}>
                        <Button
                            onClick={addBlockedSecurity}
                            color="reversed"
                            dataTestId="add-blocked-security"
                            value="Add"
                        />
                    </div>

                    <div
                        className={`${styles.saveCancelButtons} ${styles.buttons}`}
                    >
                        <Button
                            onClick={async () => {
                                store.dispatch(
                                    ConfirmActions.Open({
                                        message:
                                            "Are you sure you wish to save these changes?",
                                        title: "Update restricted securities",
                                        cancelTitle: "Cancel",
                                        okTitle: "Ok",
                                        testId: "confirm-update-blocked-security",
                                        backdrop: true,
                                        onOk: async () => {
                                            const payload = {
                                                blockedSecurities:
                                                    blockedSecuritiesSettings.map(
                                                        (entry) => {
                                                            return {
                                                                securityId:
                                                                    entry.id,
                                                                lastUpdated:
                                                                    entry.LastUpdated,
                                                                lastUser:
                                                                    entry.User,
                                                            };
                                                        }
                                                    ),
                                            };

                                            await dispatch(
                                                SettingsActions.SaveLenderBlockedSecurities(
                                                    payload
                                                )
                                            );
                                        },
                                    })
                                );
                            }}
                            dataTestId="save-blocked-securities-settings"
                            value="Save"
                            disabled={!isTraderRole}
                        />
                        <Button
                            onClick={async () => {
                                await dispatch(ModalActions.Close());
                            }}
                            color="reversedWhite"
                            dataTestId="cancel-blocked-securities-settings"
                            value="Cancel"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
